import React, { useState, useCallback, useEffect } from 'react';
import {
  Card,
  Steps,
  Button,
  Space,
  Alert,
  Descriptions,
  Statistic,
  Row,
  Col,
  Tag,
  Typography,
  Spin,
  message,
  Checkbox,
  Popconfirm,
  Modal,
  Select,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
  PlusOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { BootstrapService } from '../../services/api/bootstrapService';
import { CompanyService } from '../../services/api/companyService';
import { SubscriptionService } from '../../services/api/subscriptionService';
import {
  BootstrapResponse,
  BootstrapError,
} from '../../types/bootstrap';
import { SubscriptionType } from '../../types/subscription';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;
const { Option } = Select;

interface BootstrapTrackingTabProps {
  companyId: string | undefined;
}

// Step definitions
const BOOTSTRAP_STEPS = [
  { key: 'verify_company', title: 'Verify Company', description: 'Verifies the company exists' },
  { key: 'get_or_create_subscription', title: 'Get or Create Subscription', description: 'Gets existing subscription or creates new one' },
  { key: 'create_subscription_artifact', title: 'Create Subscription Artifact', description: 'Creates the subscription artifact (company artifact linked to prime)' },
  { key: 'create_subscription_artifact_item', title: 'Create Subscription Artifact Item', description: 'Creates the subscription artifact item (state machine instance)' },
  { key: 'build_company_model', title: 'Build Company Model', description: 'Builds the company model (creates feature artifacts from prime)' },
  { key: 'initialize_state_machines', title: 'Initialize State Machines', description: 'Initializes state machines for all artifacts' },
  { key: 'sync_features', title: 'Sync Features', description: 'Syncs company subscription features' },
];

export const BootstrapTrackingTab: React.FC<BootstrapTrackingTabProps> = ({ companyId }) => {
  const navigate = useNavigate();
  const [bootstrapResult, setBootstrapResult] = useState<BootstrapResponse | null>(null);
  const [error, setError] = useState<BootstrapError | null>(null);
  const [loading, setLoading] = useState(false);
  const [forceRebuild, setForceRebuild] = useState(false);
  const [cleanupPartial, setCleanupPartial] = useState(false);
  
  // Frontend validation state
  const [companyValidated, setCompanyValidated] = useState(false);
  const [subscriptionExists, setSubscriptionExists] = useState<boolean | null>(null);
  const [validating, setValidating] = useState(false);
  
  // Subscription creation dialog state
  const [isSubscriptionDialogVisible, setIsSubscriptionDialogVisible] = useState(false);
  const [subscriptionTypes, setSubscriptionTypes] = useState<SubscriptionType[]>([]);
  const [selectedSubscriptionTypeCode, setSelectedSubscriptionTypeCode] = useState<string>('');
  const [creatingSubscription, setCreatingSubscription] = useState(false);

  // Frontend validation: verify company and check subscription
  useEffect(() => {
    if (companyId) {
      validateCompany();
      checkSubscription();
      loadSubscriptionTypes();
    } else {
      setCompanyValidated(false);
      setSubscriptionExists(null);
    }
  }, [companyId]);

  const validateCompany = async () => {
    if (!companyId) return;
    setValidating(true);
    try {
      const company = await CompanyService.getById(companyId);
      setCompanyValidated(!!company);
    } catch (error) {
      console.error('Error validating company:', error);
      setCompanyValidated(false);
    } finally {
      setValidating(false);
    }
  };

  const checkSubscription = async () => {
    if (!companyId) return;
    try {
      const subs = await SubscriptionService.getCompanySubscriptions(companyId);
      setSubscriptionExists(subs.length > 0);
    } catch (error) {
      console.error('Error checking subscription:', error);
      setSubscriptionExists(false);
    }
  };

  const loadSubscriptionTypes = async () => {
    try {
      const types = await SubscriptionService.getSubscriptionTypes();
      setSubscriptionTypes(types.filter(type => type.is_active));
    } catch (error) {
      console.error('Error loading subscription types:', error);
      message.error('Failed to load subscription types');
    }
  };

  const handleCreateSubscription = async () => {
    if (!selectedSubscriptionTypeCode || !companyId) {
      message.warning('Please select a subscription type');
      return;
    }

    try {
      setCreatingSubscription(true);
      
      // Find the subscription type by code to get its ID
      const subscriptionType = subscriptionTypes.find(
        type => type.code === selectedSubscriptionTypeCode
      );
      
      if (!subscriptionType) {
        message.error('Subscription type not found');
        return;
      }

      // Create a company subscription using the correct endpoint
      const periodStartDate = new Date().toISOString();
      
      await SubscriptionService.createCompanySubscription({
        company_id: companyId,
        subscription_type_id: subscriptionType.id,
        period_start_date: periodStartDate,
        is_active: true,
      });
      
      message.success('Subscription created successfully');
      setIsSubscriptionDialogVisible(false);
      setSelectedSubscriptionTypeCode('');
      
      // Refresh subscription check and bootstrap status
      await checkSubscription();
      await handleBootstrap(false);
    } catch (error: any) {
      console.error('Error creating subscription:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create subscription';
      message.error(errorMessage);
    } finally {
      setCreatingSubscription(false);
    }
  };

  const handleBootstrap = useCallback(async (force: boolean = false) => {
    if (!companyId) {
      message.warning('Company ID is required');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await BootstrapService.bootstrapCompanySubscription(companyId, {
        forceRebuild: force || forceRebuild,
        cleanupPartial: cleanupPartial,
        resumeFromFailure: true,
      });
      setBootstrapResult(response);
      if (response.success) {
        message.success('Bootstrap completed successfully!');
      }
    } catch (err) {
      if (err instanceof BootstrapError) {
        setError(err);
        // Create a partial response from the error for display
        setBootstrapResult({
          success: false,
          completed_steps: err.detail.completed_steps,
          skipped_steps: [],
          failed_steps: err.detail.failed_steps,
          partial_steps: [],
          current_step: err.detail.blocker.step,
          blocker: err.detail.blocker,
          progress: err.detail.progress,
          details: {
            subscription_id: null,
            subscription_type_id: null,
            artifact_id: null,
            artifact_item_id: null,
            artifacts_created: 0,
            missing_features: 0,
          },
        });
        message.error(`Bootstrap failed: ${err.detail.message}`);
      } else {
        const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
        message.error(`Bootstrap failed: ${errorMessage}`);
        console.error('Bootstrap error:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [companyId, forceRebuild, cleanupPartial]);

  const getStepStatus = (stepKey: string): 'wait' | 'process' | 'finish' | 'error' => {
    // Frontend validation for verify_company step
    if (stepKey === 'verify_company') {
      if (companyValidated) {
        return 'finish';
      }
      if (validating) {
        return 'process';
      }
      return 'wait';
    }
    
    // Frontend validation for get_or_create_subscription step
    if (stepKey === 'get_or_create_subscription') {
      if (subscriptionExists === true) {
        return 'finish';
      }
      if (subscriptionExists === false) {
        // Show as process/waiting if no subscription exists (user needs to create one)
        return 'wait';
      }
      // If subscriptionExists is null, we're still checking
      return validating ? 'process' : 'wait';
    }
    
    // For other steps, use bootstrap result
    if (!bootstrapResult) {
      // If we have frontend validation results, show those steps as complete
      if (stepKey === 'verify_company' && companyValidated) {
        return 'finish';
      }
      if (stepKey === 'get_or_create_subscription' && subscriptionExists === true) {
        return 'finish';
      }
      return 'wait';
    }
    
    // Check if step is completed
    if (bootstrapResult.completed_steps.includes(stepKey) || bootstrapResult.skipped_steps.includes(stepKey)) {
      return 'finish';
    }
    
    // Check if step failed
    if (bootstrapResult.failed_steps.some(f => f.step === stepKey)) {
      return 'error';
    }
    
    // Check if this is the current step
    if (bootstrapResult.current_step === stepKey) {
      return 'process';
    }
    
    // Check if step is partial
    if (bootstrapResult.partial_steps.some(p => p.step === stepKey)) {
      return 'process';
    }
    
    return 'wait';
  };

  const getCurrentStepIndex = (): number => {
    // If bootstrap is successful, show all steps as complete
    if (bootstrapResult?.success) return BOOTSTRAP_STEPS.length;
    
    // If we have bootstrap result, use its current step
    if (bootstrapResult?.current_step) {
      const currentStepKey = bootstrapResult.current_step;
      const index = BOOTSTRAP_STEPS.findIndex(step => step.key === currentStepKey);
      if (index >= 0) return index;
    }
    
    // Otherwise, determine current step based on frontend validation
    // If company not validated, we're at step 0
    if (!companyValidated) return 0;
    
    // If subscription doesn't exist, we're at step 1
    if (subscriptionExists === false) return 1;
    
    // If both frontend steps are validated but no bootstrap result, start at step 2
    if (companyValidated && subscriptionExists === true && !bootstrapResult) return 2;
    
    return 0;
  };

  const getStepIcon = (stepKey: string) => {
    const status = getStepStatus(stepKey);
    switch (status) {
      case 'finish':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'error':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'process':
        return <ExclamationCircleOutlined style={{ color: '#1890ff' }} />;
      default:
        return <ClockCircleOutlined style={{ color: '#d9d9d9' }} />;
    }
  };

  const getFailedStepError = (stepKey: string): string | undefined => {
    if (!bootstrapResult) return undefined;
    const failedStep = bootstrapResult.failed_steps.find(f => f.step === stepKey);
    return failedStep?.error;
  };

  const calculateProgress = (): number => {
    const totalSteps = BOOTSTRAP_STEPS.length;
    let completedSteps = 0;
    
    // Count frontend-validated steps
    if (companyValidated) completedSteps++;
    if (subscriptionExists === true) completedSteps++;
    
    // Count backend-completed steps (excluding frontend-validated ones)
    if (bootstrapResult) {
      const backendCompleted = bootstrapResult.completed_steps.length + bootstrapResult.skipped_steps.length;
      // Don't double-count frontend-validated steps if they're also in backend response
      const frontendValidatedSteps = ['verify_company', 'get_or_create_subscription'];
      const backendOnlyCompleted = bootstrapResult.completed_steps.filter(
        step => !frontendValidatedSteps.includes(step)
      ).length + bootstrapResult.skipped_steps.length;
      completedSteps += backendOnlyCompleted;
    }
    
    return Math.round((completedSteps / totalSteps) * 100);
  };

  if (!companyId) {
    return (
      <Card>
        <Alert
          message="Company ID Required"
          description="Please select a company to view bootstrap tracking."
          type="warning"
          showIcon
        />
      </Card>
    );
  }

  return (
    <div style={{ padding: '24px', maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <Title level={4}>Bootstrap Tracking</Title>
          <Text type="secondary">
            Track the progress of company subscription bootstrap process. This is a development tool.
          </Text>
        </div>

        {/* Action Buttons */}
        <Space style={{ marginBottom: '24px' }} wrap>
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={() => handleBootstrap(false)}
            loading={loading}
            disabled={!companyId}
          >
            {bootstrapResult && !bootstrapResult.success ? 'Retry Bootstrap' : 'Run Bootstrap'}
          </Button>
          
          <Popconfirm
            title="Force Rebuild"
            description="This will rebuild everything from scratch, even if already complete. Are you sure?"
            onConfirm={() => handleBootstrap(true)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              icon={<ThunderboltOutlined />}
              loading={loading}
              disabled={!companyId}
              danger
            >
              Force Rebuild
            </Button>
          </Popconfirm>

          <Button
            icon={<ReloadOutlined />}
            onClick={() => handleBootstrap(false)}
            loading={loading}
            disabled={!companyId}
          >
            Refresh Status
          </Button>

          <Checkbox
            checked={cleanupPartial}
            onChange={(e) => setCleanupPartial(e.target.checked)}
          >
            Cleanup Partial (clean up orphaned/incomplete resources)
          </Checkbox>
        </Space>

        {/* Error Alert */}
        {error && error.detail.blocker && (
          <Alert
            type="error"
            message="Bootstrap Failed"
            description={
              <div>
                <Paragraph>
                  <Text strong>Step:</Text> {error.detail.blocker.step}
                </Paragraph>
                <Paragraph>
                  <Text strong>Error:</Text> {error.detail.blocker.error}
                </Paragraph>
                <Paragraph>
                  <Text strong>Error Type:</Text> {error.detail.blocker.error_type}
                </Paragraph>
                {error.detail.blocker.suggested_fix && (
                  <Paragraph>
                    <Text strong>Suggested Fix:</Text> {error.detail.blocker.suggested_fix}
                  </Paragraph>
                )}
                {error.detail.resume_info && (
                  <Paragraph>
                    <Text strong>Next Attempt:</Text> {error.detail.resume_info.next_attempt_will}
                  </Paragraph>
                )}
                <Space style={{ marginTop: '8px' }}>
                  <Tag color={error.detail.blocker.can_resume ? 'green' : 'red'}>
                    {error.detail.blocker.can_resume ? 'Can Resume' : 'Cannot Resume'}
                  </Tag>
                  {error.detail.blocker.requires_cleanup && (
                    <Tag color="orange">Requires Cleanup</Tag>
                  )}
                </Space>
              </div>
            }
            style={{ marginBottom: '24px' }}
            closable
            onClose={() => setError(null)}
          />
        )}

        {/* Success Alert */}
        {bootstrapResult?.success && (
          <Alert
            type="success"
            message="Bootstrap Complete"
            description="All steps completed successfully!"
            style={{ marginBottom: '24px' }}
            closable
          />
        )}

        {/* Steps Progress */}
        <Spin spinning={loading}>
          <Steps
            current={getCurrentStepIndex()}
            status={bootstrapResult?.success ? 'finish' : bootstrapResult?.blocker ? 'error' : 'process'}
            direction="vertical"
            style={{ marginBottom: '24px' }}
          >
            {BOOTSTRAP_STEPS.map((step, index) => {
              const status = getStepStatus(step.key);
              const failedError = getFailedStepError(step.key);
              const isSubscriptionStep = step.key === 'get_or_create_subscription';
              const showSubscriptionActions = isSubscriptionStep && subscriptionExists === false && !loading;
              
              return (
                <Step
                  key={step.key}
                  title={step.title}
                  description={
                    <div>
                      <div>{step.description}</div>
                      {failedError && (
                        <div style={{ marginTop: '8px' }}>
                          <Text type="danger" style={{ fontSize: '12px' }}>
                            Error: {failedError}
                          </Text>
                        </div>
                      )}
                      {status === 'finish' && (bootstrapResult || step.key === 'verify_company' || (step.key === 'get_or_create_subscription' && subscriptionExists === true)) && (
                        <div style={{ marginTop: '8px' }}>
                          <Tag color="success">Completed</Tag>
                        </div>
                      )}
                      {status === 'error' && (
                        <div style={{ marginTop: '8px' }}>
                          <Tag color="error">Failed</Tag>
                        </div>
                      )}
                      {showSubscriptionActions && (
                        <div style={{ marginTop: '12px' }}>
                          <Space>
                            <Button
                              type="primary"
                              size="small"
                              icon={<PlusOutlined />}
                              onClick={() => setIsSubscriptionDialogVisible(true)}
                            >
                              Create Subscription
                            </Button>
                            <Button
                              size="small"
                              icon={<LinkOutlined />}
                              onClick={() => {
                                navigate(`/company-management/${companyId}`, { 
                                  state: { activeTab: 'subscriptions' } 
                                });
                              }}
                            >
                              Go to Subscriptions Tab
                            </Button>
                          </Space>
                        </div>
                      )}
                      {step.key === 'verify_company' && !companyValidated && !validating && (
                        <div style={{ marginTop: '8px' }}>
                          <Tag color="warning">Company validation pending</Tag>
                        </div>
                      )}
                    </div>
                  }
                  icon={getStepIcon(step.key)}
                  status={status}
                />
              );
            })}
          </Steps>
        </Spin>

        {/* Statistics */}
        {bootstrapResult && (
          <Card title="Bootstrap Details" style={{ marginTop: '24px' }}>
            <Row gutter={16}>
              <Col span={8}>
                <Statistic
                  title="Progress"
                  value={calculateProgress()}
                  suffix="%"
                  valueStyle={{ color: bootstrapResult.success ? '#3f8600' : '#1890ff' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Completed Steps"
                  value={bootstrapResult.completed_steps.length}
                  suffix={`/ ${BOOTSTRAP_STEPS.length}`}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Failed Steps"
                  value={bootstrapResult.failed_steps.length}
                  valueStyle={{ color: bootstrapResult.failed_steps.length > 0 ? '#cf1322' : undefined }}
                />
              </Col>
            </Row>

            {bootstrapResult.details && (
              <Descriptions
                title="Bootstrap Results"
                bordered
                column={2}
                style={{ marginTop: '24px' }}
              >
                {bootstrapResult.details.subscription_id && (
                  <Descriptions.Item label="Subscription ID">
                    <Text code>{bootstrapResult.details.subscription_id}</Text>
                  </Descriptions.Item>
                )}
                {bootstrapResult.details.artifact_id && (
                  <Descriptions.Item label="Artifact ID">
                    <Text code>{bootstrapResult.details.artifact_id}</Text>
                  </Descriptions.Item>
                )}
                {bootstrapResult.details.artifact_item_id && (
                  <Descriptions.Item label="Artifact Item ID">
                    <Text code>{bootstrapResult.details.artifact_item_id}</Text>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Artifacts Created">
                  {bootstrapResult.details.artifacts_created}
                </Descriptions.Item>
                <Descriptions.Item label="Missing Features">
                  {bootstrapResult.details.missing_features}
                </Descriptions.Item>
              </Descriptions>
            )}

            {/* Progress Details */}
            <Descriptions
              title="Progress Details"
              bordered
              column={2}
              style={{ marginTop: '24px' }}
            >
              <Descriptions.Item label="Subscription Exists">
                <Tag color={bootstrapResult.progress.subscription_exists ? 'green' : 'red'}>
                  {bootstrapResult.progress.subscription_exists ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Subscription Artifact Exists">
                <Tag color={bootstrapResult.progress.subscription_artifact_exists ? 'green' : 'red'}>
                  {bootstrapResult.progress.subscription_artifact_exists ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Subscription Artifact Item Exists">
                <Tag color={bootstrapResult.progress.subscription_artifact_item_exists ? 'green' : 'red'}>
                  {bootstrapResult.progress.subscription_artifact_item_exists ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Company Model Built">
                <Tag color={bootstrapResult.progress.company_model_built ? 'green' : 'red'}>
                  {bootstrapResult.progress.company_model_built ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="State Machines Initialized">
                <Tag color={bootstrapResult.progress.state_machines_initialized ? 'green' : 'red'}>
                  {bootstrapResult.progress.state_machines_initialized ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Features Synced">
                <Tag color={bootstrapResult.progress.features_synced ? 'green' : 'red'}>
                  {bootstrapResult.progress.features_synced ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}
      </Card>

      {/* Subscription Creation Dialog */}
      <Modal
        title="Create Subscription for Company"
        open={isSubscriptionDialogVisible}
        onOk={handleCreateSubscription}
        onCancel={() => {
          setIsSubscriptionDialogVisible(false);
          setSelectedSubscriptionTypeCode('');
        }}
        confirmLoading={creatingSubscription}
        okText="Create"
        cancelText="Cancel"
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong>Select Subscription Type:</Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              placeholder="Select a subscription type"
              value={selectedSubscriptionTypeCode || undefined}
              onChange={setSelectedSubscriptionTypeCode}
              showSearch
              filterOption={(input, option) =>
                (option?.label as string || '')
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {subscriptionTypes.map((type) => {
                const label = `${type.name} (${type.code}) - ${type.billing_period}${type.base_price ? ` - $${type.base_price} ${type.currency}` : ''}`;
                return (
                  <Option key={type.id} value={type.code} label={label}>
                    {label}
                  </Option>
                );
              })}
            </Select>
            {subscriptionTypes.length === 0 && (
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                No available subscription types.
              </Text>
            )}
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              After creating the subscription, you can retry the bootstrap process.
            </Text>
          </div>
        </Space>
      </Modal>
    </div>
  );
};

